import { useState } from 'react';
import { Input } from '@servicecompressionllc/sc-design-system';
import type { Scope } from '../lib/types.js';
import {
  audienceOf,
  familyMeta,
  groupScopesByFamily,
  reachBadge,
  type ScopeAudience,
} from '../lib/scopeFamilies.js';

interface ScopeSelectorProps {
  scopes: Scope[];
  selectedScopes: string[];
  onSelectedScopesChange: (scopes: string[]) => void;
  isLoading: boolean;
  isDisabled: boolean;
  /**
   * Which principal this surface grants to. Filters the catalog to scopes the
   * principal can actually HOLD: 'user' hides app-only scopes (the server
   * refuses them on a user), 'app' hides user-only ones (e.g. My Schedule,
   * which needs a human identity). A footnote counts what was hidden.
   */
  audience: Exclude<ScopeAudience, 'both'>;
  /**
   * Fill the parent's height (flex-1) instead of the fixed h-80. Use on a
   * full page (group manage); leave off inside a modal so toggling never
   * resizes the dialog.
   */
  fill?: boolean;
}

/**
 * The scope picker — shared by groups, API keys, and direct user grants.
 *
 * Sections come from each scope's `family` (the tool-chest families), with a
 * filter box because sections make scanning work but search makes thirty
 * scopes work. The human display name leads; the raw key sits quietly beside
 * it for the technical admin.
 */
export const ScopeSelector = ({
  scopes,
  selectedScopes,
  onSelectedScopesChange,
  isLoading,
  isDisabled,
  audience,
  fill = false,
}: ScopeSelectorProps) => {
  const selected = new Set(selectedScopes);
  const [query, setQuery] = useState('');
  // Fixed height in a modal; on a full page (fill) size to content but cap at
  // the remaining viewport height and scroll past that.
  const boxHeight = fill ? 'max-h-[calc(100vh-24rem)]' : 'h-80';

  // The audience cut happens BEFORE anything renders — a hidden scope isn't
  // a smaller checkbox, it's absent, with the footnote saying why.
  const eligible = scopes.filter((s) => {
    const a = audienceOf(s);
    return a === 'both' || a === audience;
  });
  const hiddenCount = scopes.length - eligible.length;

  // Always show currently-selected scopes, even ones missing from the catalog
  // or outside this audience (grants are free strings; a granted scope must
  // stay visible and removable rather than vanishing into a blank box).
  const eligibleKeys = new Set(eligible.map((s) => s.key));
  const synthetic: Scope[] = selectedScopes
    .filter((k) => !eligibleKeys.has(k))
    .map((key) => {
      const catalogRow = scopes.find((s) => s.key === key);
      return (
        catalogRow ?? { id: `sel:${key}`, key, displayName: key, description: '', createdAt: '' }
      );
    });
  const allScopes = [...eligible, ...synthetic];

  const q = query.trim().toLowerCase();
  const visible =
    q === ''
      ? allScopes
      : allScopes.filter(
          (s) =>
            s.key.toLowerCase().includes(q) ||
            s.displayName.toLowerCase().includes(q) ||
            s.description.toLowerCase().includes(q),
        );
  const groups = groupScopesByFamily(visible);

  const toggle = (key: string, checked: boolean) => {
    onSelectedScopesChange(
      checked ? [...selectedScopes, key] : selectedScopes.filter((s) => s !== key),
    );
  };

  const hiddenNote =
    audience === 'user'
      ? `${hiddenCount} ${hiddenCount === 1 ? 'scope' : 'scopes'} only API keys can hold ${hiddenCount === 1 ? 'is' : 'are'} hidden.`
      : `${hiddenCount} ${hiddenCount === 1 ? 'scope' : 'scopes'} only people can hold ${hiddenCount === 1 ? 'is' : 'are'} hidden.`;
  const showList = !isLoading && allScopes.length > 0;

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <span className="text-sm font-medium text-ink">Scopes</span>
        {showList && (
          <Input
            aria-label="Filter scopes"
            placeholder="Filter scopes"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 py-1.5 text-sm"
          />
        )}
        <span className="ml-auto whitespace-nowrap text-xs text-ink-subtle">
          {selected.size} selected
        </span>
      </div>

      {isLoading ? (
        <div
          className={`flex ${boxHeight} items-center justify-center rounded border border-outline text-sm text-ink-muted`}
        >
          Loading scopes…
        </div>
      ) : allScopes.length === 0 ? (
        <div
          className={`flex ${boxHeight} items-center justify-center rounded border border-dashed border-outline px-6 text-center text-sm text-ink-muted`}
        >
          No scopes in the catalog yet. A SUPER_ADMIN can add them on the Scopes page.
        </div>
      ) : (
        <div className={`${boxHeight} space-y-3 overflow-y-auto`}>
          {visible.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-ink-muted">
              Nothing matches “{query.trim()}”.
            </p>
          )}
          {groups.map(({ family, scopes: familyScopes }) => {
            const meta = familyMeta(family);
            const picked = familyScopes.filter((s) => selected.has(s.key)).length;
            return (
              <section
                key={family}
                className="overflow-hidden rounded-md border border-outline"
                aria-label={meta.label}
              >
                <div
                  className="flex items-baseline justify-between bg-surface px-3.5 py-2"
                  title={meta.hint}
                >
                  <span className="text-[13px] font-semibold text-ink">{meta.label}</span>
                  {picked > 0 && (
                    <span className="text-xs text-ink-subtle">
                      {picked} of {familyScopes.length}
                    </span>
                  )}
                </div>
                {familyScopes.map((scope) => (
                  <label
                    key={scope.id}
                    title={scope.key}
                    className="flex cursor-pointer items-center gap-3 border-t border-outline px-3.5 py-2.5 hover:bg-surface-hover"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(scope.key)}
                      onChange={(e) => toggle(scope.key, e.target.checked)}
                      disabled={isDisabled}
                      className="h-4 w-4 flex-none accent-sc"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2.5">
                        <span className="text-sm text-ink">{scope.displayName}</span>
                        {reachBadge(scope.key) && (
                          <span className="rounded-full bg-warning/10 px-2 text-[10px] font-bold uppercase tracking-wide text-warning">
                            {reachBadge(scope.key)}
                          </span>
                        )}
                      </span>
                      {scope.description && (
                        <span className="block text-xs text-ink-subtle">{scope.description}</span>
                      )}
                    </span>
                  </label>
                ))}
              </section>
            );
          })}
          {hiddenCount > 0 && <p className="text-xs text-ink-subtle">{hiddenNote}</p>}
        </div>
      )}
    </div>
  );
};
